/**
 * CustomerListPage — 고객관리 목록 (전건 조회 + 벌크 저장)
 *
 * 포팅 원본: vue-spring-fw/frontend/src/pages/PageCustomerList.vue
 *
 * [DataSet]
 * - ds_list : 고객 목록 (PK: customerId, 변경추적 C/U/D)
 *
 * [서비스]
 * - customer/searchList : 조회 (inds: ds_search, outds: ds_customer → ds_list)
 * - customer/save       : 저장 (inds: ds_customer = 변경분)
 *
 * 【사용처】 router/index.tsx — `{ path: 'customer' }`
 */
import { useEffect, useMemo, useState } from 'react'
import { Form, Input, Tag, Modal, Space, Select } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useFramework } from '@/framework/useFramework'
import { useCodes } from '@/framework/useCodes'
import { useExcel } from '@/framework/useExcel'
import { useLabel } from '@/framework/useLabel'
import { buildExcelHeaders } from '@/framework/grid/excelHeaders'
import RowStateTag from '@/components/common/RowStateTag'
import { useI18nTickStore } from '@/framework/useLocale'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'
import FormSelect from '@/components/common/FormSelect'
import FormInput from '@/components/common/FormInput'

interface CustomerRow {
  /** 고객 PK — Oracle SEQ 채번. C 행은 비어있음(저장 시 백엔드에서 채워서 outds 로 회신). */
  customerId?: string
  /** 고객명 — 필수 */
  customerName?: string
  /** 고객유형 — 공통코드 CUSTOMER_TYPE (CORP/INDIV/etc.) */
  customerType?: string
  /** 연락처 — 000-0000-0000 패턴 검증 */
  phone?: string
  /** 이메일 — type=email 검증 */
  email?: string
  /** 지역 — 공통코드 REGION (SEOUL/BUSAN/etc.) */
  region?: string
  /** 등급 — 공통코드 CUSTOMER_GRADE (VIP/GOLD/SILVER/NORMAL) */
  grade?: string
  /** 주소 — 자유 입력 */
  address?: string
}

const gradeColor: Record<string, string> = {
  VIP: 'red',
  GOLD: 'orange',
  SILVER: 'blue',
  NORMAL: 'default'
}

/**
 * CustomerListPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function CustomerListPage() {
  const { $createDataSet, $transaction, $msg } = useFramework()
  const { getCodeName, getOptions, loadCodes } = useCodes()
  const { exportToExcel } = useExcel()
  const { t } = useLabel()
  useI18nTickStore((s) => s.tick)

  const ds_list = $createDataSet<CustomerRow>([])

  const [searchForm] = Form.useForm()
  const [editOpen, setEditOpen] = useState(false)
  const [editMode, setEditMode] = useState<'C' | 'U'>('C')
  const [editingIndex, setEditingIndex] = useState<number>(-1)
  const [editForm] = Form.useForm<CustomerRow>()
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // ─── 공통코드 lazy load ─
    // 라이프사이클: mount → loadCodes(병렬 fetch+캐시) → 그리드 render 시 getCodeName 으로 코드명 해석
    loadCodes(['CUSTOMER_TYPE', 'CUSTOMER_GRADE', 'REGION'])
  }, [loadCodes])

  /** 검색 — 폼 조건으로 ds_list 조회 + 선택 해제. */
  const fn_search = async () => {
    // ─── 검색 조건 빌드 + 트랜잭션 ─
    // 1) form 값 수집 (안 채운 필드는 undefined → 백엔드에서 전체/부분 매칭)
    const params = searchForm.getFieldsValue()
    // 2) 로딩 표시 (그리드 spinner)
    setLoading(true)
    // 3) RPC 호출 — outds 로 ds_customer 페이로드를 ds_list 에 직접 바인딩 (변경상태 reset)
    const res = await $transaction({
      id: 'select',
      service: 'customer/searchList',
      action: 'search',
      params,
      outds: { ds_customer: ds_list }
    })
    setLoading(false)
    // 4) 성공 시 행 선택 초기화 (조회 결과 변경으로 stale key 제거)
    if (res.success) setSelectedRowKeys([])
  }

  /** 검색 초기화 — 폼 reset + ds 비우기 + 선택 해제. */
  const fn_reset = () => {
    // ─── 폼/DS/선택 모두 초기 상태로 ─
    searchForm.resetFields()
    ds_list.clear()
    setSelectedRowKeys([])
  }

  /** 신규 — 편집 모달을 C 모드로 열고 기본값 주입. */
  const fn_add = () => {
    // ─── 편집 모달 C 모드 진입 ─
    // 1) 모드/인덱스 reset
    setEditMode('C')
    setEditingIndex(-1)
    // 2) 폼 reset 후 합리적 기본값 주입 (CORP/SEOUL/NORMAL)
    editForm.resetFields()
    editForm.setFieldsValue({
      customerType: 'CORP',
      region: 'SEOUL',
      grade: 'NORMAL'
    })
    // 3) 모달 open
    setEditOpen(true)
  }

  /** 수정 — 단일 선택 행을 편집 모달 U 모드로 로딩. */
  const fn_edit = () => {
    // ─── 단일 선택 검증 + 편집 모달 U 모드 ─
    // 1) 선택행 1개 검증 (0/2개 이상이면 안내 후 종료)
    if (selectedRowKeys.length !== 1) {
      $msg.byId('MSG_SELECT_EDIT_ROW')
      return
    }
    // 2) visibleRows 에서 _rowId 매칭 (filter/sort 영향 고려)
    const idx = visibleRows.findIndex((r) => r._rowId === selectedRowKeys[0])
    if (idx < 0) return
    // 3) 모드/인덱스 set + 폼에 현재 행 데이터 주입
    setEditMode('U')
    setEditingIndex(idx)
    editForm.setFieldsValue(visibleRows[idx].data)
    setEditOpen(true)
  }

  /** 편집 모달 OK — C 면 addRow, U 면 visible→actual 인덱스 매핑 후 updateRow. */
  const fn_editSubmit = async () => {
    // ─── 폼 검증 + ds 반영 ─
    // 1) AntD Form validate (실패 시 throw → 모달 유지)
    const values = await editForm.validateFields()
    if (editMode === 'C') {
      // 2-C) 신규 — addRow → DataSet 내부에서 _state='C' 마킹
      ds_list.addRow(values)
    } else {
      // 2-U) 수정 — visible 인덱스를 actual rows 인덱스로 재매핑 (filter/sort 차이 보정)
      const targetId = visibleRows[editingIndex]?._rowId
      const actualIdx = ds_list.rows.findIndex((r) => r._rowId === targetId)
      // updateRow → 기존 _state='C' 유지, 'N' 이면 'U' 로 전이
      if (actualIdx >= 0) ds_list.updateRow(actualIdx, values)
    }
    // 3) 모달 close (취소 케이스는 onCancel)
    setEditOpen(false)
  }

  /** 삭제 — 선택 행을 일괄 deleteRow (확인 다이얼로그 후). */
  const fn_delete = () => {
    // ─── 선택 검증 + confirm + 일괄 삭제 ─
    // 1) 선택 0건이면 안내 후 종료
    if (!selectedRowKeys.length) {
      $msg.byId('MSG_SELECT_DELETE_ROW')
      return
    }
    // 2) confirm 다이얼로그 → onOk 콜백 안에서 일괄 삭제
    $msg.confirmById('MSG_CONFIRM_DELETE', () => {
      // 3) 키 -> actual idx 매핑 후 deleteRow
      //    deleteRow: _state='N' → 즉시 제거, _state='C/U' → 'D' 로 마킹 후 visibleRows 에서 숨김
      for (const key of selectedRowKeys) {
        const actualIdx = ds_list.rows.findIndex((r) => r._rowId === key)
        if (actualIdx >= 0) ds_list.deleteRow(actualIdx)
      }
      // 4) 선택 초기화
      setSelectedRowKeys([])
    })
  }

  /** 저장 — 변경분(ds_list)을 customer/save 로 일괄 전송. */
  const fn_save = async () => {
    // ─── dirty 체크 + 벌크 저장 ─
    // 1) 변경분 없으면 안내 후 종료 (불필요 RPC 방지)
    if (!ds_list.isDirty) {
      $msg.byId('MSG_NO_CHANGES')
      return
    }
    // 2) inds: ds_customer = ds_list (DataSet 직렬화 시 변경분만 추출)
    //    outds: 갱신 결과(채번된 PK 등)를 다시 ds_list 로 회신
    const res = await $transaction({
      id: 'save',
      service: 'customer/save',
      action: 'save',
      inds: { ds_customer: ds_list },
      outds: { ds_customer: ds_list }
    })
    // 3) 성공 시 안내 + 재조회 (서버 trigger/SEQ 반영분 동기화)
    if (res.success) {
      $msg.byId('MSG_SAVE_SUCCESS')
      fn_search()
    }
  }

  /** 취소 — 변경 폐기 후 재조회 (DB 상태로 복원). */
  const fn_cancel = () => {
    fn_search()
  }

  /** Excel Export — 화면 표시 행을 i18n 헤더로 xlsx 출력. */
  const fn_export = () => {
    exportToExcel(
      visibleRows.map((r) => r.data as unknown as Record<string, unknown>),
      buildExcelHeaders(
        [
          { key: 'customerId', labelKey: 'COL_CUSTOMER_ID', fallback: '고객ID', width: 12 },
          { key: 'customerName', labelKey: 'COL_CUSTOMER_NAME', fallback: '고객명', width: 20 },
          { key: 'customerType', labelKey: 'COL_TYPE', fallback: '유형', width: 12 },
          { key: 'phone', labelKey: 'COL_PHONE', fallback: '연락처', width: 16 },
          { key: 'email', labelKey: 'COL_EMAIL', fallback: '이메일', width: 24 },
          { key: 'region', labelKey: 'COL_REGION', fallback: '지역', width: 10 },
          { key: 'grade', labelKey: 'COL_GRADE', fallback: '등급', width: 10 },
          { key: 'address', labelKey: 'COL_ADDRESS', fallback: '주소', width: 30 }
        ],
        t
      ),
      t('FILE_CUSTOMER_LIST', 'customers.xlsx')
    )
  }

  const visibleRows = ds_list.getVisibleRows()

  const columns: ColumnsType<(typeof visibleRows)[number]> = useMemo(
    () => [
      { title: t('COL_CUSTOMER_ID', '고객ID'), dataIndex: ['data', 'customerId'], width: 110 },
      { title: t('COL_CUSTOMER_NAME', '고객명'), dataIndex: ['data', 'customerName'], width: 160 },
      {
        title: t('COL_CUSTOMER_TYPE', '유형'),
        dataIndex: ['data', 'customerType'],
        width: 100,
        render: (v: string) => getCodeName('CUSTOMER_TYPE', v)
      },
      { title: t('COL_PHONE', '연락처'), dataIndex: ['data', 'phone'], width: 140 },
      { title: t('COL_EMAIL', '이메일'), dataIndex: ['data', 'email'], width: 200 },
      {
        title: t('COL_REGION', '지역'),
        dataIndex: ['data', 'region'],
        width: 100,
        render: (v: string) => getCodeName('REGION', v)
      },
      {
        title: t('COL_GRADE', '등급'),
        dataIndex: ['data', 'grade'],
        width: 90,
        render: (v: string) => (
          <Tag color={gradeColor[v] ?? 'default'}>{getCodeName('CUSTOMER_GRADE', v)}</Tag>
        )
      },
      { title: t('COL_ADDRESS', '주소'), dataIndex: ['data', 'address'] },
      {
        title: t('COL_STATUS', '상태'),
        dataIndex: '_state',
        width: 70,
        render: (s: string) => <RowStateTag state={s} updateColor="gold" />
      }
    ],
    [getCodeName, t]
  )

  useEffect(() => {
    // ─── 초기 로드 (mount 1회) ─
    // 라이프사이클: mount → fn_search → DataSet 채움 → 그리드 렌더
    fn_search()
  }, []) // deps 비움 — fn_search 가 매 render 새 함수여도 1회만

  return (
    <>
      <AppPage
        search={{
          form: searchForm,
          onSearch: fn_search,
          onReset: fn_reset,
          loading,
          content: (
            <>
              <Form.Item name="keyword" label={t('LBL_KEYWORD', '키워드')}>
                <FormInput
                  placeholder={t('PH_CUSTOMER_NAME', '고객명/사업자번호/대표자명')}
                  style={{ width: 200 }}
                />
              </Form.Item>
              <Form.Item name="customerType" label={t('LBL_CUSTOMER_TYPE', '고객유형')}>
                <FormSelect
                  mode="multiple"
                  options={getOptions('CUSTOMER_TYPE')}
                  style={{ minWidth: 180 }}
                  placeholder={t('PH_ALL', '전체')}
                />
              </Form.Item>
              <Form.Item name="region" label={t('LBL_REGION', '지역')}>
                <FormSelect
                  mode="multiple"
                  options={getOptions('REGION')}
                  style={{ minWidth: 180 }}
                  placeholder={t('PH_ALL', '전체')}
                />
              </Form.Item>
              <Form.Item name="grade" label={t('LBL_GRADE', '등급')}>
                <FormSelect
                  mode="multiple"
                  options={getOptions('CUSTOMER_GRADE')}
                  style={{ minWidth: 160 }}
                  placeholder={t('PH_ALL', '전체')}
                />
              </Form.Item>
            </>
          )
        }}
        toolbar={{
          onSearch: fn_search,
          onAdd: fn_add,
          onEdit: fn_edit,
          onDelete: fn_delete,
          onSave: fn_save,
          onCancel: fn_cancel,
          onExport: fn_export,
          loading,
          dirty: ds_list.isDirty
        }}
      >
        <DataTable
          rowKey={(r) => r._rowId}
          columns={columns}
          dataSource={visibleRows}
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys)
          }}
        />
      </AppPage>

      <Modal
        open={editOpen}
        title={editMode === 'C' ? '고객 추가' : '고객 수정'}
        onOk={fn_editSubmit}
        onCancel={() => setEditOpen(false)}
        okText={t('BTN_CONFIRM', '확인')}
        cancelText={t('BTN_CANCEL', '취소')}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          {editMode === 'U' && (
            <Form.Item name="customerId" label={t('COL_CUSTOMER_ID', '고객ID')}>
              <Input disabled />
            </Form.Item>
          )}
          <Form.Item
            name="customerName"
            label={t('LBL_CUSTOMER_NAME', '고객명')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="customerType" label={t('COL_TYPE', '유형')} rules={[{ required: true }]}>
            <Select options={getOptions('CUSTOMER_TYPE')} />
          </Form.Item>
          <Space.Compact block>
            <Form.Item
              name="phone"
              label={t('COL_PHONE', '연락처')}
              rules={[{ pattern: /^\d{2,3}-\d{3,4}-\d{4}$/, message: '000-0000-0000' }]}
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="email" label={t('COL_EMAIL', '이메일')} rules={[{ type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="region" label={t('COL_REGION', '지역')}>
            <Select options={getOptions('REGION')} />
          </Form.Item>
          <Form.Item name="grade" label={t('COL_GRADE', '등급')}>
            <Select options={getOptions('CUSTOMER_GRADE')} />
          </Form.Item>
          <Form.Item name="address" label={t('COL_ADDRESS', '주소')}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
